import { useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Heading, Text, Button, Flex, Box, VStack, Divider, Checkbox, Badge, Spinner, Alert, AlertIcon,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCuratedPlaylists,
  getPlaylistMembership,
  batchAddTracksToPlaylist,
  batchRemoveTracksFromPlaylist,
} from '../../../api/libraryApi';
import { PlaylistMultiSelect } from './PlaylistMultiSelect';

export interface ManagePlaylistsModalProps {
  open: boolean;
  /** Track content ids the operation applies to. */
  trackIds: string[];
  onClose: () => void;
  /** Called after a successful apply so the caller can refresh/clear selection. */
  onDone?: () => void;
  /** Cosmetic context for the affected-count copy (e.g. "playlist"). */
  sourceLabel?: string;
}

/**
 * Bulk "manage playlists" tool — sibling of BulkEditTracksModal. Adds the selected tracks to one or
 * more curated playlists and/or removes them from playlists they're in. Two-step (compose → confirm)
 * with an affected-track count; add is idempotent (already-in tracks are skipped server-side), remove
 * is destructive (guarded by a confirm + warning). Membership drives the remove list + "already in".
 */
export function ManagePlaylistsModal({ open, trackIds, onClose, onDone, sourceLabel }: ManagePlaylistsModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [addIds, setAddIds] = useState<string[]>([]);
  const [removeIds, setRemoveIds] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const affectedCount = trackIds.length;
  const membershipKey = useMemo(() => [...trackIds].sort().join(','), [trackIds]);

  const { data: membership = [], isLoading: membershipLoading } = useQuery({
    queryKey: ['playlist-membership', membershipKey],
    queryFn: () => getPlaylistMembership(trackIds),
    enabled: open && trackIds.length > 0,
    staleTime: 30_000,
  });

  const { data: playlists = [] } = useQuery({
    queryKey: ['curated-playlists-picker'],
    queryFn: fetchCuratedPlaylists,
    enabled: open,
  });

  const playlistNameById = useMemo(
    () => new Map(playlists.map((p) => [p.id, p.name])),
    [playlists],
  );
  const memberCountByPlaylist = useMemo(
    () => new Map(membership.map((m) => [m.playlistId, m.memberTrackIds.length])),
    [membership],
  );

  function reset() {
    setAddIds([]);
    setRemoveIds([]);
    setShowConfirm(false);
  }
  function handleClose() {
    if (applyMutation.isPending) return;
    reset();
    onClose();
  }

  // Mutual exclusion: a playlist can't be both an add target and a remove target.
  function handleAddChange(ids: string[]) {
    setAddIds(ids);
    setRemoveIds((prev) => prev.filter((r) => !ids.includes(r)));
  }
  function toggleRemove(playlistId: string) {
    setRemoveIds((prev) => {
      const next = prev.includes(playlistId) ? prev.filter((p) => p !== playlistId) : [...prev, playlistId];
      return next;
    });
    setAddIds((prev) => prev.filter((a) => a !== playlistId));
  }

  const hasAnyOp = addIds.length > 0 || removeIds.length > 0;

  const applyMutation = useMutation({
    mutationFn: async () => {
      await Promise.all([
        ...addIds.map((pid) => batchAddTracksToPlaylist(pid, trackIds)),
        ...removeIds.map((pid) => batchRemoveTracksFromPlaylist(pid, trackIds)),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-playlist'] });
      queryClient.invalidateQueries({ queryKey: ['playlist-membership'] });
      queryClient.invalidateQueries({ queryKey: ['curated-playlists-picker'] });
      onDone?.();
      reset();
      onClose();
    },
  });

  return (
    <Modal isOpen={open} onClose={handleClose} isCentered scrollBehavior="inside" size="xl">
      <ModalOverlay />
      <ModalContent data-testid="manage-playlists-modal" maxW="640px" borderRadius="15px">
        <ModalHeader>
          <Heading as="h3" size="md">{t('admin.playlists.manage.title')}</Heading>
          <Text color="gray.600" fontSize="sm" fontWeight="400" mt={1} data-testid="manage-playlists-affected-count">
            {t('admin.library.bulkEdit.affectedCount', { count: affectedCount, context: sourceLabel })}
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={applyMutation.isPending} />

        <ModalBody>
          {!showConfirm ? (
            <VStack align="stretch" spacing={5}>
              {applyMutation.isError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">{t('admin.playlists.manage.failed')}</Text>
                </Alert>
              )}

              {/* Add */}
              <Box>
                <Text fontWeight="600" fontSize="sm" mb={2}>{t('admin.playlists.manage.addSection')}</Text>
                <PlaylistMultiSelect value={addIds} onChange={handleAddChange} hideLabel />
              </Box>

              <Divider />

              {/* Remove — only playlists the selection touches */}
              <Box>
                <Text fontWeight="600" fontSize="sm" mb={2}>{t('admin.playlists.manage.removeSection')}</Text>
                {membershipLoading ? (
                  <Flex justify="center" p={3}><Spinner size="sm" /></Flex>
                ) : membership.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" data-testid="manage-playlists-remove-empty">
                    {t('admin.playlists.manage.removeEmpty')}
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={1} maxH="200px" overflowY="auto">
                    {membership.map((m) => (
                      <Flex
                        key={m.playlistId}
                        as="label"
                        align="center"
                        gap={3}
                        p="6px 8px"
                        borderRadius="8px"
                        _hover={{ bg: 'gray.50' }}
                        data-testid={`manage-playlists-remove-${m.playlistId}`}
                      >
                        <Checkbox
                          isChecked={removeIds.includes(m.playlistId)}
                          onChange={() => toggleRemove(m.playlistId)}
                          colorScheme="red"
                        />
                        <Text flex={1} fontSize="sm" noOfLines={1}>{m.playlistName}</Text>
                        <Badge colorScheme="gray">
                          {t('admin.playlists.manage.removesFrom', { count: m.memberTrackIds.length, total: affectedCount })}
                        </Badge>
                      </Flex>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4} data-testid="manage-playlists-confirm-step">
              {removeIds.length > 0 && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">{t('admin.playlists.manage.removeWarning')}</Text>
                </Alert>
              )}

              {addIds.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="600" mb={1}>{t('admin.playlists.manage.confirmAddHeading')}</Text>
                  <VStack align="stretch" spacing={1}>
                    {addIds.map((pid) => {
                      const already = memberCountByPlaylist.get(pid) ?? 0;
                      return (
                        <Text key={pid} fontSize="sm" color="gray.700" data-testid={`manage-playlists-confirm-add-${pid}`}>
                          • {playlistNameById.get(pid) ?? pid}
                          {already > 0 && (
                            <Text as="span" color="gray.500"> {' '}— {t('admin.playlists.manage.alreadyIn', { count: already })}</Text>
                          )}
                        </Text>
                      );
                    })}
                  </VStack>
                </Box>
              )}

              {removeIds.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="600" mb={1}>{t('admin.playlists.manage.confirmRemoveHeading')}</Text>
                  <VStack align="stretch" spacing={1}>
                    {removeIds.map((pid) => (
                      <Text key={pid} fontSize="sm" color="gray.700" data-testid={`manage-playlists-confirm-remove-${pid}`}>
                        • {playlistNameById.get(pid) ?? pid}
                        <Text as="span" color="gray.500"> {' '}({t('admin.playlists.manage.removesFrom', { count: memberCountByPlaylist.get(pid) ?? 0, total: affectedCount })})</Text>
                      </Text>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>

        <Divider />

        <ModalFooter>
          {!showConfirm ? (
            <Flex justify="space-between" align="center" w="100%">
              <Button variant="outline" onClick={handleClose} data-testid="manage-playlists-cancel">
                {t('common.cancel')}
              </Button>
              <Button
                variant="brand"
                onClick={() => setShowConfirm(true)}
                isDisabled={!hasAnyOp || affectedCount === 0}
                data-testid="manage-playlists-review"
              >
                {t('admin.library.bulkEdit.review')}
              </Button>
            </Flex>
          ) : (
            <Flex justify="space-between" align="center" w="100%">
              <Button variant="outline" onClick={() => setShowConfirm(false)} isDisabled={applyMutation.isPending} data-testid="manage-playlists-back">
                {t('common.back')}
              </Button>
              <Button
                variant="brand"
                onClick={() => applyMutation.mutate()}
                isDisabled={!hasAnyOp || applyMutation.isPending}
                data-testid="manage-playlists-apply"
              >
                {applyMutation.isPending ? <Spinner size="sm" /> : t('admin.playlists.manage.apply')}
              </Button>
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ManagePlaylistsModal;
